import { useHotkeys } from "@mantine/hooks";
import { Button } from "@win-reality/react-ui";
import { useDispatch, useSelector } from "react-redux";

import {
  incrementTaggingFlowStep,
  setTaggingFlowStep,
} from "../../../../store/slices/hotkeys";
import {
  putTag,
  removeTag,
  setTaggingState,
} from "../../../../store/slices/session";
import type { RootState } from "../../../../store/store";

/**
 * PitchResultStep - Select pitch result
 *
 * Options:
 * - Strike L (L), Strike Sw (S)
 * - Ball (B), Foul (F)
 * - Foul Tip (T), Catcher I. (C)
 * - HBP (H), In Play (I)
 *
 * Special logic:
 * - Strikeouts on 2 strikes automatically set playResult and outsRecorded
 */
export const PitchResultStep = () => {
  const tagData = useSelector((state: RootState) => state.session.tagData);
  const gameState = useSelector((state: RootState) => state.game.gameState);
  const dispatch = useDispatch();
  const tagKey = "pitchResult";

  function handleTag(value: string) {
    dispatch(setTaggingState(true));
    if (tagData[tagKey] === value) {
      dispatch(removeTag({ key: tagKey }));
    } else if (
      ["StrikeSwinging", "StrikeCalled", "FoulTip"].includes(value) &&
      gameState.count.strikes === 2
    ) {
      // Strikeout logic - automatically set playResult and outsRecorded
      dispatch(putTag({ key: tagKey, value }));
      dispatch(putTag({ key: "outsRecorded", value: 1 }));
      if (value === "StrikeSwinging" || value === "FoulTip") {
        dispatch(putTag({ key: "playResult", value: "StrikeoutSwinging" }));
      }
      if (value === "StrikeCalled") {
        dispatch(putTag({ key: "playResult", value: "StrikeoutLooking" }));
      }
      dispatch(incrementTaggingFlowStep());
    } else {
      dispatch(putTag({ key: tagKey, value }));
      dispatch(incrementTaggingFlowStep());
    }
  }

  const hotkeys = {
    StrikeSwinging: "S",
    StrikeCalled: "L",
    BallCalled: "B",
    FoulTip: "T",
    HitByPitch: "H",
    Foul: "F",
    CatchersInt: "C",
    InPlay: "I",
  };

  useHotkeys([
    [hotkeys.StrikeSwinging, () => handleTag("StrikeSwinging")],
    [hotkeys.StrikeCalled, () => handleTag("StrikeCalled")],
    [hotkeys.BallCalled, () => handleTag("BallCalled")],
    [hotkeys.FoulTip, () => handleTag("FoulTip")],
    [hotkeys.HitByPitch, () => handleTag("HitByPitch")],
    [hotkeys.Foul, () => handleTag("Foul")],
    [hotkeys.CatchersInt, () => handleTag("CatchersInt")],
    [hotkeys.InPlay, () => handleTag("InPlay")],
  ]);

  return (
    <div className="flex w-full flex-col items-stretch gap-0">
      {/* Previously completed tags - clickable to jump back */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        className="flex h-10 cursor-pointer items-center justify-center"
        onClick={() => dispatch(setTaggingFlowStep(0))}
      >
        <p className="text-sm font-bold text-blue">
          {tagData.prePitchStance ?? "---"}
        </p>
      </div>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        className="flex h-10 cursor-pointer items-center justify-center"
        onClick={() => dispatch(setTaggingFlowStep(1))}
      >
        <p className="text-sm font-bold text-blue">
          {tagData.pitchType ?? "---"}
        </p>
      </div>

      {/* Instructions */}
      <div className="flex h-10 items-center justify-center">
        <p className="text-sm italic text-text-light">Select Pitch Result</p>
      </div>

      {/* Button Grid - 2 columns */}
      <div className="flex w-full gap-0">
        {/* Left Column */}
        <div className="flex flex-1 flex-col gap-0">
          <div className="flex h-10 items-center justify-center gap-2 px-2">
            <Button
              variant={tagData[tagKey] === "StrikeCalled" ? "blue" : "ghost"}
              onClick={() => handleTag("StrikeCalled")}
              size="sm"
              className="flex-1 normal-case border border-gray-300"
            >
              <span className="flex items-center justify-between w-full">
                Strike L
                <kbd className="ml-2 rounded bg-[hsla(218,100%,52%,0.1)] px-2 py-1 text-xs font-semibold">
                  {hotkeys.StrikeCalled}
                </kbd>
              </span>
            </Button>
          </div>

          <div className="flex h-10 items-center justify-center gap-2 px-2">
            <Button
              variant={tagData[tagKey] === "BallCalled" ? "blue" : "ghost"}
              onClick={() => handleTag("BallCalled")}
              size="sm"
              className="flex-1 normal-case border border-gray-300"
            >
              <span className="flex items-center justify-between w-full">
                Ball
                <kbd className="ml-2 rounded bg-[hsla(218,100%,52%,0.1)] px-2 py-1 text-xs font-semibold">
                  {hotkeys.BallCalled}
                </kbd>
              </span>
            </Button>
          </div>

          <div className="flex h-10 items-center justify-center gap-2 px-2">
            <Button
              variant={tagData[tagKey] === "FoulTip" ? "blue" : "ghost"}
              onClick={() => handleTag("FoulTip")}
              size="sm"
              className="flex-1 normal-case border border-gray-300"
            >
              <span className="flex items-center justify-between w-full">
                Foul Tip
                <kbd className="ml-2 rounded bg-[hsla(218,100%,52%,0.1)] px-2 py-1 text-xs font-semibold">
                  {hotkeys.FoulTip}
                </kbd>
              </span>
            </Button>
          </div>

          <div className="flex h-10 items-center justify-center gap-2 px-2">
            <Button
              variant={tagData[tagKey] === "HitByPitch" ? "blue" : "ghost"}
              onClick={() => handleTag("HitByPitch")}
              size="sm"
              className="flex-1 normal-case border border-gray-300"
            >
              <span className="flex items-center justify-between w-full">
                HBP
                <kbd className="ml-2 rounded bg-[hsla(218,100%,52%,0.1)] px-2 py-1 text-xs font-semibold">
                  {hotkeys.HitByPitch}
                </kbd>
              </span>
            </Button>
          </div>
        </div>

        {/* Right Column */}
        <div className="flex flex-1 flex-col gap-0">
          <div className="flex h-10 items-center justify-center gap-2 px-2">
            <Button
              variant={tagData[tagKey] === "StrikeSwinging" ? "blue" : "ghost"}
              onClick={() => handleTag("StrikeSwinging")}
              size="sm"
              className="flex-1 normal-case border border-gray-300"
            >
              <span className="flex items-center justify-between w-full">
                Strike Sw
                <kbd className="ml-2 rounded bg-[hsla(218,100%,52%,0.1)] px-2 py-1 text-xs font-semibold">
                  {hotkeys.StrikeSwinging}
                </kbd>
              </span>
            </Button>
          </div>

          <div className="flex h-10 items-center justify-center gap-2 px-2">
            <Button
              variant={tagData[tagKey] === "Foul" ? "blue" : "ghost"}
              onClick={() => handleTag("Foul")}
              size="sm"
              className="flex-1 normal-case border border-gray-300"
            >
              <span className="flex items-center justify-between w-full">
                Foul
                <kbd className="ml-2 rounded bg-[hsla(218,100%,52%,0.1)] px-2 py-1 text-xs font-semibold">
                  {hotkeys.Foul}
                </kbd>
              </span>
            </Button>
          </div>

          <div className="flex h-10 items-center justify-center gap-2 px-2">
            <Button
              variant={tagData[tagKey] === "CatchersInt" ? "blue" : "ghost"}
              onClick={() => handleTag("CatchersInt")}
              size="sm"
              className="flex-1 normal-case border border-gray-300"
            >
              <span className="flex items-center justify-between w-full">
                Catcher I.
                <kbd className="ml-2 rounded bg-[hsla(218,100%,52%,0.1)] px-2 py-1 text-xs font-semibold">
                  {hotkeys.CatchersInt}
                </kbd>
              </span>
            </Button>
          </div>

          <div className="flex h-10 items-center justify-center gap-2 px-2">
            <Button
              variant={tagData[tagKey] === "InPlay" ? "blue" : "ghost"}
              onClick={() => handleTag("InPlay")}
              size="sm"
              className="flex-1 normal-case border border-gray-300"
            >
              <span className="flex items-center justify-between w-full">
                In Play
                <kbd className="ml-2 rounded bg-[hsla(218,100%,52%,0.1)] px-2 py-1 text-xs font-semibold">
                  {hotkeys.InPlay}
                </kbd>
              </span>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
